import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Truck, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { storeAuthToken } from '../utils/auth';
import { formatUsPhoneInput } from '../utils/phone';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealerAuth = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choice'); // 'choice', 'login', 'signup', 'preview'
  const [isLoading, setIsLoading] = useState(false);
  
  // Login form
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    stayLoggedIn: false
  });
  
  // Signup form
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    business_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    website: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      // Store token using auth utility
      storeAuthToken(response.data.token, loginData.stayLoggedIn);

      // Force password change on first login after a super-admin reset.
      if (response.data?.must_change_password) {
        toast.warning('Please choose a new password to finish signing in.');
        navigate('/change-password?forced=1');
        return;
      }

      toast.success('Welcome back!');
      navigate('/dealer');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const digitsOnlyPhone = signupData.phone ? signupData.phone.replace(/\D/g, '').slice(-10) : '';
      const isPreview = mode === 'preview';

      const response = await axios.post(`${API}/dealer/register`, {
        email: signupData.email,
        password: signupData.password,
        name: signupData.name,
        business_name: signupData.business_name,
        phone: digitsOnlyPhone,
        address: signupData.address,
        city: signupData.city,
        state: signupData.state,
        zipcode: signupData.zipcode,
        website: signupData.website,
        trial: isPreview,
      });

      storeAuthToken(response.data.token, true); // Persistent login for new registrations
      if (isPreview) {
        toast.success('Preview access unlocked — explore the dashboard freely.');
        navigate('/dealer');
      } else {
        toast.success('Account created! Choose your subscription plan.');
        navigate('/dealers');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial choice screen
  if (mode === 'choice') {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center p-4 relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-15 blur-[140px]" />
          <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-amber-500 opacity-15 blur-[140px]" />
        </div>
        <div className="relative bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <BrandLogo size="lg" variant="stacked" />
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400 mb-2">Advisor Portal</p>
            <p className="text-slate-400 text-sm">Access your advisor dashboard</p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => setMode('login')}
              className="w-full py-6 text-lg bg-[#F2A900] hover:bg-amber-400 text-black font-bold rounded-full"
              data-testid="dealer-login-btn"
            >
              Sign In
            </Button>

            <Button
              onClick={() => setMode('signup')}
              variant="outline"
              className="w-full py-6 text-lg bg-white/5 border border-white/15 text-white hover:bg-white/10 rounded-full"
              data-testid="dealer-signup-btn"
            >
              Create Advisor Account
            </Button>

            {/* Preview mode — dealers can explore the dashboard before paying.
                No leads assigned + write actions gated behind a subscribe CTA. */}
            <button
              onClick={() => setMode('preview')}
              className="w-full py-3 text-sm text-cyan-300 hover:text-cyan-200 underline underline-offset-4 transition-colors"
              data-testid="dealer-preview-btn"
            >
              or <span className="font-semibold">start a free dashboard preview</span> — no card required
            </button>
          </div>

          <p className="text-center text-sm text-slate-400 mt-6">
            Not an advisor?{' '}
            <button
              onClick={() => navigate('/')}
              className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
            >
              Take the RV Match
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Login screen
  if (mode === 'login') {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center p-4 relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-15 blur-[140px]" />
          <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-amber-500 opacity-15 blur-[140px]" />
        </div>
        <div className="relative bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] p-8 w-full max-w-md">
          <button
            onClick={() => setMode('choice')}
            className="flex items-center gap-1 text-slate-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-extrabold text-white tracking-tight" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              ADVISOR SIGN IN
            </h1>
          </div>

          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full bg-white/5 border border-white/15 text-white hover:bg-white/10 rounded-full"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/auth/callback?target=dealer';
                const authBase = process.env.REACT_APP_AUTH_SERVICE_URL;
                window.location.href = `${authBase}/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-dealer-btn"
            >
              Continue with Google
            </Button>

            <div className="flex items-center gap-3">
              <div className="h-px bg-white/10 flex-1" />
              <span className="text-xs text-slate-500">or</span>
              <div className="h-px bg-white/10 flex-1" />
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="login-email" className="text-slate-300">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="advisor@example.com"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="login-password" className="text-slate-300">Password</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                data-testid="login-password"
              />
              <div className="text-right mt-1">
                <Link
                  to="/forgot-password"
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  data-testid="dealer-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="stay-logged-in"
                checked={loginData.stayLoggedIn}
                onChange={(e) => setLoginData({...loginData, stayLoggedIn: e.target.checked})}
                className="w-4 h-4 text-[#F2A900] bg-white/5 border-white/20 rounded focus:ring-[#F2A900]"
                data-testid="stay-logged-in"
              />
              <Label htmlFor="stay-logged-in" className="text-sm text-slate-400 cursor-pointer">
                Stay logged in
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full py-5 bg-[#F2A900] hover:bg-amber-400 text-black font-bold rounded-full"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
              Sign In
            </Button>
          </form>

          <p className="text-center text-sm text-slate-400 mt-6">
            Don't have an account?{' '}
            <button
              onClick={() => setMode('signup')}
              className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
            >
              Create one
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Signup screen
  return (
    <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center p-4 relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-15 blur-[140px]" />
        <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-amber-500 opacity-15 blur-[140px]" />
      </div>
      <div className="relative bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto [&_label]:text-slate-300 [&_input]:bg-white/5 [&_input]:border-white/10 [&_input]:text-white [&_input::placeholder]:text-slate-500">
        <button
          onClick={() => setMode('choice')}
          className="flex items-center gap-1 text-slate-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft size={18} />
          Back
        </button>
        
        <div className="text-center mb-6">
          <h1 className="text-2xl font-extrabold text-white tracking-tight" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            {mode === 'preview' ? 'START YOUR FREE PREVIEW' : 'CREATE ADVISOR ACCOUNT'}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {mode === 'preview'
              ? 'Explore every feature. Upgrade only when you\u2019re ready to receive real leads.'
              : 'Fill in your business information'}
          </p>
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          {/* Account Information */}
          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
            <h3 className="font-semibold text-amber-400 mb-3">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="signup-email">Email Address *</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="advisor@example.com"
                  value={signupData.email}
                  onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                  required
                  data-testid="signup-email"
                />
              </div>
              <div>
                <Label htmlFor="signup-password">Password *</Label>
                <Input
                  id="signup-password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={signupData.password}
                  onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                  required
                  data-testid="signup-password"
                />
              </div>
              <div>
                <Label htmlFor="signup-confirm">Confirm Password *</Label>
                <Input
                  id="signup-confirm"
                  type="password"
                  placeholder="Confirm password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                  required
                  data-testid="signup-confirm"
                />
              </div>
            </div>
          </div>
          
          {/* Business Information */}
          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
            <h3 className="font-semibold text-amber-400 mb-3">Business Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="signup-name">Contact Name *</Label>
                <Input
                  id="signup-name"
                  type="text"
                  placeholder="John Smith"
                  value={signupData.name}
                  onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                  required
                  data-testid="signup-name"
                />
              </div>
              <div>
                <Label htmlFor="signup-business">Business Name *</Label>
                <Input
                  id="signup-business"
                  type="text"
                  placeholder="Smith's RV World"
                  value={signupData.business_name}
                  onChange={(e) => setSignupData({...signupData, business_name: e.target.value})}
                  required
                  data-testid="signup-business"
                />
              </div>
              <div>
                <Label htmlFor="signup-phone">Phone Number *</Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={signupData.phone}
                  onChange={(e) => setSignupData({ ...signupData, phone: formatUsPhoneInput(e.target.value) })}
                  required
                  data-testid="signup-phone"
                />
              </div>
              <div>
                <Label htmlFor="signup-website">Website (optional)</Label>
                <Input
                  id="signup-website"
                  type="url"
                  placeholder="https://www.yoursite.com"
                  value={signupData.website}
                  onChange={(e) => setSignupData({...signupData, website: e.target.value})}
                  data-testid="signup-website"
                />
              </div>
            </div>
          </div>
          
          {/* Address Information */}
          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
            <h3 className="font-semibold text-amber-400 mb-3">Business Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="signup-address">Street Address *</Label>
                <Input
                  id="signup-address"
                  type="text"
                  placeholder="123 Main Street"
                  value={signupData.address}
                  onChange={(e) => setSignupData({...signupData, address: e.target.value})}
                  required
                  data-testid="signup-address"
                />
              </div>
              <div>
                <Label htmlFor="signup-city">City *</Label>
                <Input
                  id="signup-city"
                  type="text"
                  placeholder="Austin"
                  value={signupData.city}
                  onChange={(e) => setSignupData({...signupData, city: e.target.value})}
                  required
                  data-testid="signup-city"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="signup-state">State *</Label>
                  <Input
                    id="signup-state"
                    type="text"
                    placeholder="TX"
                    maxLength={2}
                    value={signupData.state}
                    onChange={(e) => setSignupData({...signupData, state: e.target.value.toUpperCase()})}
                    required
                    data-testid="signup-state"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-zipcode">Zipcode *</Label>
                  <Input
                    id="signup-zipcode"
                    type="text"
                    placeholder="78701"
                    value={signupData.zipcode}
                    onChange={(e) => setSignupData({...signupData, zipcode: e.target.value})}
                    required
                    data-testid="signup-zipcode"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full py-5 bg-[#F2A900] hover:bg-amber-400 text-black font-bold text-lg rounded-full"
            disabled={isLoading}
            data-testid="signup-submit"
          >
            {isLoading ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
            {mode === 'preview' ? 'Start My Preview' : 'Create Account & Choose Plan'}
          </Button>

          <p className="text-center text-xs text-slate-500">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </form>

        <p className="text-center text-sm text-slate-400 mt-4">
          Already have an account?{' '}
          <button
            onClick={() => setMode('login')}
            className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default DealerAuth;
